import { MimshakData } from "../interfaces/DTO/mimshakData";
import { Product } from "../interfaces/DTO/mislaka_xml_models";

export interface DistinctContactInfo {
  client_names: string[];  // רשימת client_name ייחודיים
  last_names: string[];    // רשימת last_name ייחודיים
  phones: string[];        // רשימת טלפונים ייחודיים
  emails: string[];        // רשימת מיילים ייחודיים
}

export interface AddressInfo {
  street?: string;
  house_number?: string;
  city?: string;
  postal_code?: string;
}

export class MimshakContactUtil {
  /**
   * מחלץ client_name ייחודיים מכל המוצרים
   */
  static getDistinctClientNames(products: Product[]): string[] {
    const namesSet = new Set<string>();

    products.forEach((product) => {
      if (product.client?.client_name) {
        const name = product.client.client_name.trim();
        if (name) namesSet.add(name);
      }
    });

    return Array.from(namesSet);
  }

  /**
   * מחלץ last_name ייחודיים מכל המוצרים
   */
  static getDistinctLastNames(products: Product[]): string[] {
    const namesSet = new Set<string>();

    products.forEach((product) => {
      if (product.client?.last_name) {
        const name = product.client.last_name.trim();
        if (name) namesSet.add(name);
      }
    });

    return Array.from(namesSet);
  }

  /**
   * מחלץ טלפונים ייחודיים מכל המוצרים
   */
  static getDistinctPhones(products: Product[]): string[] {
    const phonesSet = new Set<string>();

    products.forEach((product) => {
      if (product.client) {
        const client = product.client;

        if (client.telephone) {
          const phone = client.telephone.trim();
          if (phone) phonesSet.add(phone);
        }

        if (client.cellular) {
          const phone = client.cellular.trim();
          if (phone) phonesSet.add(phone);
        }
      }
    });

    return Array.from(phonesSet);
  }

  /**
   * מחלץ מיילים ייחודיים מכל המוצרים (ללא הבדלי אותיות גדולות/קטנות)
   */
  static getDistinctEmails(products: Product[]): string[] {
    const emails = new Map<string, string>();
    products.forEach((product) => {
      const email = product.client?.email?.trim();
      if (email && !emails.has(email.toLowerCase())) emails.set(email.toLowerCase(), email);
    });
    return Array.from(emails.values());
  }

  /**
   * מחלץ כתובות ייחודיות מכל המוצרים
   */
  static getDistinctAddresses(products: Product[]): AddressInfo[] {
    const addressesMap = new Map<string, AddressInfo>();

    products.forEach((product) => {
      if (product.client) {
        const client = product.client;

        if (client.street || client.city) {
          const address: AddressInfo = {
            street: client.street,
            house_number: client.house_number,
            city: client.city,
            postal_code: client.postal_code,
          };

          const key = `${address.street || ""}_${address.house_number || ""}_${address.city || ""}_${address.postal_code || ""}`;
          if (key !== "___" && !addressesMap.has(key)) {
            addressesMap.set(key, address);
          }
        }
      }
    });

    return Array.from(addressesMap.values());
  }

  /**
   * מחלץ מידע ייחודי (שמות וטלפונים) מרשימת מימשקים של קונטקט ספציפי
   */
  static getDistinctContactInfo(mimshakDataList: MimshakData[]): DistinctContactInfo {
    const allProducts = mimshakDataList.flatMap((md) => md.data?.products || []);

    return {
      client_names: this.getDistinctClientNames(allProducts),
      last_names: this.getDistinctLastNames(allProducts),
      phones: this.getDistinctPhones(allProducts),
      emails: this.getDistinctEmails(allProducts),
    };
  }

  /**
   * מחלץ מידע לפי contact_id
   */
  static getDistinctContactInfoByContactId(
    allMimshakData: MimshakData[],
    contactId: number
  ): DistinctContactInfo {
    const filteredData = allMimshakData.filter(
      (md) => md.contact_id === contactId
    );
    return this.getDistinctContactInfo(filteredData);
  }

  /**
   * מחלץ כתובות ייחודיות לפי contact_id
   */
  static getDistinctAddressesByContactId(
    allMimshakData: MimshakData[],
    contactId: number
  ): AddressInfo[] {
    const filteredData = allMimshakData.filter(
      (md) => md.contact_id === contactId
    );
    const allProducts = filteredData.flatMap((md) => md.data?.products || []);
    return this.getDistinctAddresses(allProducts);
  }

  /**
   * משווה בין שם פרטי ושם משפחה שמורים לשמות שנמצאו במימשק
   * מחזיר רשימה של client_names ו-last_names שונים
   */
  static getDifferentNames(
    savedFirstName: string,
    savedLastName: string,
    mimshakClientNames: string[],
    mimshakLastNames: string[]
  ): { client_names: string[]; last_names: string[] } {
    const normalizedSavedFirstName = savedFirstName.trim().toLowerCase();
    const normalizedSavedLastName = savedLastName.trim().toLowerCase();
    const savedFullName = `${normalizedSavedFirstName} ${normalizedSavedLastName}`.trim();

    // סינון client_names שונים
    const differentClientNames = mimshakClientNames.filter((name) => {
      const normalizedName = name.trim().toLowerCase();
      return (
        normalizedName !== "" &&
        normalizedName !== savedFullName &&
        normalizedName !== normalizedSavedFirstName &&
        normalizedName !== normalizedSavedLastName
      );
    });

    // סינון last_names שונים
    const differentLastNames = mimshakLastNames.filter((name) => {
      const normalizedName = name.trim().toLowerCase();
      return (
        normalizedName !== "" &&
        normalizedName !== savedFullName &&
        normalizedName !== normalizedSavedLastName
      );
    });

    return {
      client_names: differentClientNames,
      last_names: differentLastNames,
    };
  }

  /**
   * משווה בין מייל שמור למיילים שנמצאו במימשק ומחזיר מיילים שונים
   */
  static getDifferentEmails(savedEmail: string, mimshakEmails: string[]): string[] {
    const normalizedSavedEmail = savedEmail.trim().toLowerCase();
    return mimshakEmails.filter((email) => email.trim() !== "" && email.trim().toLowerCase() !== normalizedSavedEmail);
  }

  /**
   * משווה בין טלפון שמור לטלפונים שנמצאו במימשק ומחזיר טלפונים שונים
   */
  static getDifferentPhones(savedPhone: string, mimshakPhones: string[]): string[] {
    const normalizedSavedPhone = savedPhone.trim().replace(/[^0-9]/g, "");

    return mimshakPhones.filter((phone) => {
      const normalizedPhone = phone.trim().replace(/[^0-9]/g, "");
      return normalizedPhone !== normalizedSavedPhone && normalizedPhone !== "";
    });
  }
}
